/**
 * Les briques des onglets de la fiche enfant côté équipe.
 *
 * Un champ porte deux sortes de messages (voir `regles.ts`) : en rouge ce qui
 * empêche d'enregistrer, en orange ce qui manquait déjà et que le parent
 * complétera.
 */

import React, { useId } from 'react'

export interface Messages {
  bloquantes: Record<string, string>
  manques: Record<string, string>
}

const CHAMP = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm'

function bordure(messages: Messages | undefined, cle: string | undefined) {
  if (!messages || !cle) return 'border-gray-300'
  if (messages.bloquantes[cle]) return 'border-red-400 bg-red-50/40'
  if (messages.manques[cle]) return 'border-amber-400 bg-amber-50/40'
  return 'border-gray-300'
}

export function Message({ messages, cle }: { messages?: Messages; cle?: string }) {
  if (!messages || !cle) return null
  if (messages.bloquantes[cle]) return <p className="mt-1 text-xs text-red-600">{messages.bloquantes[cle]}</p>
  if (messages.manques[cle]) {
    return <p className="mt-1 text-xs text-amber-700">À compléter : {messages.manques[cle]}</p>
  }
  return null
}

export function Section({ titre, children, intro }: { titre: string; children: React.ReactNode; intro?: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{titre}</h3>
        {intro && <p className="mt-1 text-sm text-gray-500">{intro}</p>}
      </div>
      {children}
    </section>
  )
}

export function Libelle({ htmlFor, children, requis }: { htmlFor?: string; children: React.ReactNode; requis?: boolean }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
      {children}
      {requis && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  )
}

export function Aide({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-xs text-gray-500">{children}</p>
}

/** Une réponse d'avant le formulaire des parents, montrée à côté du champ qui ne sait pas la lire. */
export function AncienneReponse({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600">
      Ancienne réponse : {children}
    </p>
  )
}

export function Saisie({
  label, valeur, onChange, cle, messages, requis, type = 'text', placeholder, aide, className,
}: {
  label: string; valeur: string; onChange: (v: string) => void; cle?: string; messages?: Messages
  requis?: boolean; type?: string; placeholder?: string; aide?: React.ReactNode; className?: string
}) {
  const id = useId()
  return (
    <div className={className}>
      <Libelle htmlFor={id} requis={requis}>{label}</Libelle>
      <input
        id={id}
        type={type}
        value={valeur || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${CHAMP} ${bordure(messages, cle)}`}
      />
      <Message messages={messages} cle={cle} />
      {aide && <Aide>{aide}</Aide>}
    </div>
  )
}

export function ZoneTexte({
  label, valeur, onChange, cle, messages, requis, aide, className = 'sm:col-span-2',
}: {
  label: string; valeur: string; onChange: (v: string) => void; cle?: string; messages?: Messages
  requis?: boolean; aide?: React.ReactNode; className?: string
}) {
  const id = useId()
  return (
    <div className={className}>
      <Libelle htmlFor={id} requis={requis}>{label}</Libelle>
      <textarea
        id={id}
        value={valeur || ''}
        onChange={e => onChange(e.target.value)}
        rows={3}
        className={`${CHAMP} resize-y ${bordure(messages, cle)}`}
      />
      <Message messages={messages} cle={cle} />
      {aide && <Aide>{aide}</Aide>}
    </div>
  )
}

export function Liste({
  label, valeur, onChange, options, vide, cle, messages, requis, aide, className,
}: {
  label: string; valeur: string; onChange: (v: string) => void
  options: { valeur: string; libelle: string }[]; vide?: string
  cle?: string; messages?: Messages; requis?: boolean; aide?: React.ReactNode; className?: string
}) {
  const id = useId()
  return (
    <div className={className}>
      <Libelle htmlFor={id} requis={requis}>{label}</Libelle>
      <select
        id={id}
        value={valeur}
        onChange={e => onChange(e.target.value)}
        className={`${CHAMP} bg-white ${bordure(messages, cle)}`}
      >
        {vide !== undefined && <option value="">{vide}</option>}
        {options.map(o => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
      </select>
      <Message messages={messages} cle={cle} />
      {aide && <Aide>{aide}</Aide>}
    </div>
  )
}

export function Interrupteur({
  label, coche, onChange, aide, cle, messages, className,
}: {
  label: React.ReactNode; coche: boolean; onChange: (v: boolean) => void
  aide?: React.ReactNode; cle?: string; messages?: Messages; className?: string
}) {
  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-4 py-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <button
          type="button"
          role="switch"
          aria-checked={coche}
          onClick={() => onChange(!coche)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
            coche ? 'bg-amber-600' : 'bg-gray-300'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            coche ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
      <Message messages={messages} cle={cle} />
      {aide && <Aide>{aide}</Aide>}
    </div>
  )
}

/** Une case à cocher avec sa phrase, pour les déclarations (accord de l'autre parent, réponses du parent). */
export function Case({
  label, coche, onChange, aide, cle, messages,
}: {
  label: React.ReactNode; coche: boolean; onChange: (v: boolean) => void
  aide?: React.ReactNode; cle?: string; messages?: Messages
}) {
  const id = useId()
  return (
    <div>
      <div className="flex items-start gap-2">
        <input
          id={id}
          type="checkbox"
          checked={coche}
          onChange={e => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
        />
        <label htmlFor={id} className="text-sm text-gray-700">{label}</label>
      </div>
      <div className="pl-6">
        <Message messages={messages} cle={cle} />
        {aide && <Aide>{aide}</Aide>}
      </div>
    </div>
  )
}

/** Oui / Non sans réponse par défaut : « non » est une réponse, l'absence de réponse n'en est pas une. */
export function OuiNon({
  valeur, onChange, cle, messages,
}: {
  valeur: 'oui' | 'non' | null; onChange: (v: 'oui' | 'non') => void; cle?: string; messages?: Messages
}) {
  const bouton = (v: 'oui' | 'non', libelle: string) => (
    <button
      type="button"
      aria-pressed={valeur === v}
      onClick={() => onChange(v)}
      className={`px-3 py-1.5 text-sm font-medium border first:rounded-l-lg last:rounded-r-lg -ml-px first:ml-0 transition-colors ${
        valeur === v
          ? v === 'oui'
            ? 'bg-green-600 border-green-600 text-white'
            : 'bg-gray-700 border-gray-700 text-white'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
      }`}
    >
      {libelle}
    </button>
  )
  return (
    <div>
      <div className="inline-flex">
        {bouton('oui', 'Oui')}
        {bouton('non', 'Non')}
      </div>
      <Message messages={messages} cle={cle} />
    </div>
  )
}
